// @ts-nocheck
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, Calendar, BarChart3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { entryCountsInOperationalFinance } from "@/utils/financialOperationalImpact";

interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  status: string;
  created_at: string;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  payment_notes?: string;
}

interface RecurringEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  due_day: number;
  is_active: boolean;
  created_at: string;
  client_id?: string;
  case_id?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface CashFlowChartProps {
  entries: FinancialEntry[];
  recurringEntries: RecurringEntry[];
}

const CashFlowChart = ({ entries, recurringEntries }: CashFlowChartProps) => {
  const [allEntries, setAllEntries] = useState<FinancialEntry[]>([]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
   };

  // Função para gerar entradas virtuais baseadas nas recorrentes
  const generateVirtualEntries = async () => {
    const virtualEntries: FinancialEntry[] = [];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // Mês 1-12
    const currentYear = currentDate.getFullYear();

    // Buscar pagamentos existentes para o mês atual
    const { data: existingPayments } = await supabase
      .from('recurring_payments')
      .select('recurring_entry_id')
      .eq('payment_month', currentMonth)
      .eq('payment_year', currentYear);

    const paidRecurringIds = new Set(existingPayments?.map(p => p.recurring_entry_id) || []);

    recurringEntries
      .filter(entry => entry.is_active && !paidRecurringIds.has(entry.id))
      .forEach(entry => {
        // Criar entrada virtual para o mês atual
        const virtualEntry: FinancialEntry = {
          id: `virtual-${entry.id}-${currentMonth - 1}-${currentYear}`, // currentMonth - 1 para manter compatibilidade com getMonth()
          description: `${entry.description} (Recorrente)`,
          amount: entry.amount,
          entry_type: entry.entry_type,
          category: entry.category,
          status: 'Pendente', // Entradas recorrentes são consideradas pendentes até confirmadas
          created_at: new Date(currentYear, currentMonth - 1, entry.due_day).toISOString(),
          clients: entry.clients,
          cases: entry.cases
        };
        virtualEntries.push(virtualEntry);
      });

    return virtualEntries;
  };

  // Função para atualizar as entradas combinadas
  const updateAllEntries = async () => {
    const virtualEntries = await generateVirtualEntries();
    const operational = entries.filter(entryCountsInOperationalFinance);
    setAllEntries([...operational, ...virtualEntries]);
  };

  // Atualizar entradas combinadas quando entries ou recurringEntries mudarem
  useEffect(() => {
    if (entries.length > 0 || recurringEntries.length > 0) {
      updateAllEntries();
    }
  }, [entries, recurringEntries]);

  // Preparar dados para fluxo de caixa mensal
  const prepareMonthlyData = () => {
    const monthlyData: { [key: string]: { revenues: number; expenses: number; month: string } } = {};
    
    allEntries.forEach(entry => {
      const date = new Date(entry.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('pt-BR', { year: 'numeric', month: 'short' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { revenues: 0, expenses: 0, month: monthName };
      }
      
      if (entry.entry_type === 'receita') {
        monthlyData[monthKey].revenues += entry.amount;
      } else {
        monthlyData[monthKey].expenses += entry.amount;
      }
    });

    return Object.keys(monthlyData)
      .sort()
      .slice(-12) // Últimos 12 meses
      .map(key => ({
        ...monthlyData[key],
        netFlow: monthlyData[key].revenues - monthlyData[key].expenses
      }));
  };

  // Preparar dados para fluxo de caixa acumulado
  const prepareCumulativeData = () => {
    const monthlyData = prepareMonthlyData();
    let cumulativeFlow = 0;
    
    return monthlyData.map(data => {
      cumulativeFlow += data.netFlow;
      return {
        ...data,
        cumulativeFlow
      };
    });
  };

  // Preparar dados por categoria
  const prepareCategoryData = () => {
    const categoryData: { [key: string]: number } = {};
    
    allEntries.forEach(entry => {
      if (!categoryData[entry.category]) {
        categoryData[entry.category] = 0;
      }
      categoryData[entry.category] += entry.amount;
    });

    return Object.entries(categoryData)
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8); // Top 8 categorias
  };

  // Preparar dados de status de pagamento
  const prepareStatusData = () => {
    const revenues = allEntries.filter(e => e.entry_type === 'receita');
    const statusData: { [key: string]: number } = {};
    
    revenues.forEach(entry => {
      if (!statusData[entry.status]) {
        statusData[entry.status] = 0;
      }
      statusData[entry.status] += entry.amount;
    });

    return Object.entries(statusData)
      .map(([status, amount]) => ({ status, amount }));
  };

  const monthlyData = prepareMonthlyData();
  const cumulativeData = prepareCumulativeData();
  const categoryData = prepareCategoryData();
  const statusData = prepareStatusData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card p-3 border rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="cashflow" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="cashflow" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Fluxo de Caixa
          </TabsTrigger>
          <TabsTrigger value="cumulative" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Acumulado
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Categorias
          </TabsTrigger>
          <TabsTrigger value="status">
            Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cashflow">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Fluxo de Caixa Mensal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis
                      dataKey="month"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tickFormatter={formatCurrency}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="revenues"
                      name="Receitas"
                      stroke="hsl(var(--tertiary))"
                      fill="url(#colorRevenues)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="expenses"
                      name="Despesas"
                      stroke="hsl(var(--destructive))"
                      fill="url(#colorExpenses)"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="netFlow"
                      name="Fluxo Líquido"
                      stroke="hsl(var(--primary))"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                    />
                    <defs>
                      <linearGradient id="colorRevenues" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--tertiary))" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="hsl(var(--tertiary))" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cumulative">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Fluxo de Caixa Acumulado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={cumulativeData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="month" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      tickFormatter={formatCurrency}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="cumulativeFlow" 
                      name="Fluxo Acumulado"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary) / 0.1)"
                      strokeWidth={3}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="netFlow" 
                      name="Fluxo Mensal"
                      stroke="hsl(var(--secondary))"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Análise por Categoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      type="number"
                      tickFormatter={formatCurrency}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      type="category"
                      dataKey="category" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="amount" 
                      name="Valor Total"
                      fill="hsl(var(--secondary))"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="status">
          <Card>
            <CardHeader>
              <CardTitle>Status de Recebimentos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="status" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      tickFormatter={formatCurrency}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="amount" 
                      name="Valor"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CashFlowChart;